import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxPaginationModule } from 'ngx-pagination';
import { ElectricalStandardDto, ElectricalStandardsService, GetElectricalStandardsInput } from '@shared/service-proxies/electrical-standards.service';

@Component({
  selector: 'app-electrical-standards-modal',
  templateUrl: './electrical-standards-modal.component.html',
  styleUrls: ['./electrical-standards-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NgxPaginationModule]
})
export class ElectricalStandardsModalComponent implements OnInit {

  public standards: ElectricalStandardDto[] = [];
  public searchTerm: string = '';
  public isLoading: boolean = false;
  public searchLoading: boolean = false;
  public searchTimer?: number;
  public hasError: boolean = false;
  public errorMessage: string = '';
  public offset = 0;
  public openingId: number | null = null;

  public standardsPaginationConfig = {
    id: 'electrical-standards-pagination',
    itemsPerPage: 5,
    currentPage: 1,
    totalItems: 0
  };

  constructor(
    public activeModal: NgbActiveModal,
    private electricalStandardsService: ElectricalStandardsService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.loadStandards();
  }

  public onSearchTermChanged(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = window.setTimeout(() => {
      this.loadStandards(true);
    }, 800);
  }

  public async loadStandards(isSearch = false): Promise<void> {
    if (this.searchTimer && !isSearch) {
      clearTimeout(this.searchTimer);
      this.searchTimer = undefined;
    }

    if (isSearch) {
      this.standardsPaginationConfig.currentPage = 1;
      this.offset = 0;
      this.searchLoading = true;
    } else {
      this.isLoading = true;
    }

    this.hasError = false;
    this.errorMessage = '';

    try {
      const result = await this.electricalStandardsService.get({
        offset: this.offset,
        pageSize: this.standardsPaginationConfig.itemsPerPage,
        searchParms: this.searchTerm?.trim() || ''
      } as GetElectricalStandardsInput);

      this.standards = result?.items ?? [];
      this.standardsPaginationConfig.totalItems = result?.totalCount ?? 0;
    }
    catch (error) {
      this.hasError = true;
      this.errorMessage = 'Unable to load electrical standards right now.';
      this.standards = [];
      this.standardsPaginationConfig.totalItems = 0;
    }
    finally {
      if (isSearch) {
        this.searchLoading = false;
      } else {
        this.isLoading = false;
      }
    }
  }

  public standardsPageChanged(page: number): void {
    this.standardsPaginationConfig.currentPage = page;
    this.offset = (page - 1) * this.standardsPaginationConfig.itemsPerPage;
    this.loadStandards(false);
  }

  public async getStandardUrl(id: string): Promise<void> {
    const numericId = Number(id);
    this.openingId = numericId;
    try {
      const result = await this.electricalStandardsService.createSignUrl(numericId);
      window.open(result.url, '_blank');
    }
    catch (error) {
      this.hasError = true;
      this.errorMessage = 'Unable to get the signed URL for the electrical standard.';
    }
    finally {
      this.openingId = null;
    }
  }

  public getStatusLabel(statusTag?: string): string | null {
    if (statusTag === 'current') {
      return 'Current';
    }
    if (statusTag === 'superseded') {
      return 'Superseded';
    }
    return null;
  }

  public getStatusTagClass(statusTag?: string): string {
    if (statusTag === 'current') {
      return 'status-tag--current';
    }
    if (statusTag === 'superseded') {
      return 'status-tag--superseded';
    }
    return '';
  }
}
